import { SSO_PENDING_ACTIONS_KEY, getTokenKey } from '@common/constants/shared';
import { setLoadingPage } from '@common/redux/slice/page';
import { loginSuccess } from '@common/redux/slice/auth';
import io from '@common/services/featherIO/feathers.io';
import { showAlert } from '@common/utils/ShowAlert';
import { actionPromise } from '@common/utils/handleActions/excuteAction';
import { isCanvas } from '@common/utils/screen';
import AsyncStorage from '@react-native-async-storage/async-storage';
import qs from 'query-string';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { EmitterSubscription, Linking, Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@common/redux/hooks';
import { loadingPageSelector } from '@common/redux/selectors/page';

let eventDeepLinkUrl: EmitterSubscription;

type Props = { dependencies: Record<string, any> };

type PendingSsoActions = {
  objectId?: string;
  screenUuid?: string;
  successActionId?: string;
  failedActionId?: string;
  successActions?: any[];
  failedActions?: any[];
};

const resolveSsoErrorMessage = ({
  ssoError,
  ssoErrorDescription,
  messages,
}: {
  ssoError: string | null;
  ssoErrorDescription?: string | null;
  messages: Record<string, any>;
}) => {
  if (ssoError && typeof messages?.[ssoError] === 'string') {
    return messages[ssoError] as string;
  }
  if (ssoErrorDescription) return ssoErrorDescription;
  if (ssoError) return ssoError;
  return messages?.SSO_AUTH_FAILED as string;
};

const pickFirst = (value: string | string[] | null) => {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] || null;
  return value;
};

function useSamlAppAction({ dependencies }: Props) {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const loadingPage = useAppSelector(loadingPageSelector);

  /**
   * Read the SSO result.
   *
   * The backend returns it in the URL fragment, never the query string: a
   * fragment is not sent to the server, stays out of access logs and is not
   * forwarded in the Referer header.
   */
  const getCallbackParams = (url: any) => {
    try {
      const rawHash =
        Platform.OS === 'web'
          ? window.parent.location.hash || window.location.hash
          : (url?.url || '').split('#')[1] || '';
      const params = qs.parse(rawHash.replace(/^#/, ''));

      return {
        token: pickFirst(params?.sso_token as string | string[] | null),
        ssoError: pickFirst(params?.sso_error as string | string[] | null),
        ssoErrorDescription: pickFirst(
          params?.sso_error_description as string | string[] | null
        ),
      };
    } catch (error) {
      console.error('===getCallbackParams ERROR', error);
      return { token: null, ssoError: null, ssoErrorDescription: null };
    }
  };

  /** Drop the result from the address bar so a reload cannot replay it. */
  const clearCallbackFragment = () => {
    if (Platform.OS !== 'web') return;
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      console.error('===clearCallbackFragment ERROR', error);
    }
  };

  const runChain = async (
    pending: PendingSsoActions | null,
    which: 'success' | 'failed'
  ) => {
    if (!pending) return;
    const actionId =
      which === 'success' ? pending.successActionId : pending.failedActionId;
    const arrayAction =
      which === 'success' ? pending.successActions : pending.failedActions;
    if (!actionId || !arrayAction?.length) return;

    await actionPromise({
      objectId: pending.objectId,
      actionId,
      clientValues: dependencies,
      screenUuid: pending.screenUuid || '',
      screenAction: false,
      messages,
      arrayAction,
    });
  };

  const handleUrlDeepLink = async (url: any) => {
    const { token, ssoError, ssoErrorDescription } = getCallbackParams(url);
    if (!token && !ssoError) return;

    dispatch(setLoadingPage(true));

    let pending: PendingSsoActions | null = null;
    try {
      const raw = await AsyncStorage.getItem(SSO_PENDING_ACTIONS_KEY);
      pending = raw ? JSON.parse(raw) : null;

      if (ssoError || !token) {
        const errorMessage = resolveSsoErrorMessage({
          ssoError,
          ssoErrorDescription,
          messages,
        });
        if (errorMessage) showAlert(errorMessage);
        await runChain(pending, 'failed');
        return;
      }

      await AsyncStorage.setItem(getTokenKey(), token);
      const authResult: any = await io.authenticate({
        strategy: 'jwt',
        accessToken: token,
      });

      const user = authResult?.userPreview || authResult?.user;
      if (user) {
        dispatch(loginSuccess({ user }));
      }

      await runChain(pending, 'success');
    } catch (error: any) {
      console.error('===handleSamlAppCallback ERROR', error);
      const errorMessage =
        error?.message?.toString() || (messages?.SSO_AUTH_FAILED as string);
      if (errorMessage) showAlert(errorMessage);
      // A failure after a valid assertion - a rejected token, a broken action
      // chain - is still a failed login from the app's point of view.
      await runChain(pending, 'failed').catch(() => undefined);
    } finally {
      await AsyncStorage.removeItem(SSO_PENDING_ACTIONS_KEY);
      clearCallbackFragment();
      dispatch(setLoadingPage(false));
    }
  };

  useEffect(() => {
    Linking.getInitialURL().then((url) => handleUrlDeepLink({ url }));

    eventDeepLinkUrl = Linking.addEventListener('url', (e) =>
      handleUrlDeepLink(e)
    );

    return () => {
      if (eventDeepLinkUrl) {
        eventDeepLinkUrl.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (!isCanvas()) {
      if (loadingPage) return;
      handleUrlDeepLink(null);
    }
  }, [loadingPage]);
}

export default useSamlAppAction;
